import { useEffect, useState } from "react";
import { Link, NavLink, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Menu, X, Search, Compass, LayoutGrid, Scale, Calculator, BookOpen } from "lucide-react";
import { LOGO_URL } from "../../lib/api";
import { getCompare } from "../../lib/compareStore";
import LiveSearch from "./LiveSearch";

export default function Header() {
  const { t } = useTranslation();
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);
  const [compareCount, setCompareCount] = useState(0);
  const navigate = useNavigate();

  const navPrimary = [
    { to: "/kategoriler", label: t("nav.categories"), icon: LayoutGrid, key: "categories" },
    { to: "/acilis-rehberi", label: t("nav.guide"), icon: Compass, key: "guide" },
    { to: "/oda-tipleri", label: t("nav.rooms"), icon: LayoutGrid, key: "rooms" },
    { to: "/hesaplama", label: t("nav.calculator"), icon: Calculator, key: "calc" },
    { to: "/karsilastirma", label: t("nav.compare"), icon: Scale, key: "compare" },
    { to: "/blog", label: t("nav.blog"), icon: BookOpen, key: "blog" },
  ];

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 8);
    onScroll();
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    const update = () => setCompareCount(getCompare().length);
    update();
    window.addEventListener("compare:update", update);
    return () => window.removeEventListener("compare:update", update);
  }, []);

  return (
    <header
      className={`sticky top-0 z-40 transition-all duration-300 ${scrolled ? "bg-navy/95 backdrop-blur-md shadow-lg" : "bg-navy"}`}
      data-testid="site-header"
    >
      <div className="container-site flex items-center gap-4 py-4">
        <Link to="/" className="flex items-center gap-3" data-testid="header-logo-link">
          <div className="bg-navy border border-white/10 rounded-md p-1.5 h-12 w-12 flex items-center justify-center">
            <img src={LOGO_URL} alt="Otel Pro" className="h-8 w-auto" />
          </div>
          <div className="hidden sm:flex flex-col leading-tight">
            <span className="heading-serif text-white text-lg font-semibold">Otel Pro</span>
            <span className="text-[10px] uppercase tracking-[0.18em] text-gold">{t("tagline")}</span>
          </div>
        </Link>

        <nav className="hidden lg:flex items-center gap-1 ml-auto mr-4" data-testid="primary-nav">
          {navPrimary.map((n) => (
            <NavLink
              key={n.to}
              to={n.to}
              className={({ isActive }) =>
                `flex items-center gap-1.5 px-3 py-2 text-[13px] tracking-wide transition-colors rounded-md ${
                  isActive ? "text-gold" : "text-white/80 hover:text-gold"
                }`
              }
              data-testid={`nav-${n.key}`}
            >
              <n.icon size={14} /> {n.label}
            </NavLink>
          ))}
        </nav>

        <button
          onClick={() => setSearchOpen((s) => !s)}
          className="text-white/80 hover:text-gold transition-colors p-2"
          aria-label={t("search.button")}
          data-testid="header-search-toggle"
        >
          <Search size={18} />
        </button>

        <Link
          to="/karsilastirma"
          className="relative hidden md:flex items-center gap-1.5 text-white/80 hover:text-gold transition-colors px-2"
          data-testid="header-compare-badge"
        >
          <Scale size={18} />
          {compareCount > 0 && (
            <span className="absolute -top-1 -right-1 bg-gold text-white text-[10px] font-bold rounded-full h-4 w-4 flex items-center justify-center">
              {compareCount}
            </span>
          )}
        </Link>

        <Link to="/iletisim" className="hidden md:inline-flex btn-gold !py-2 !px-5 text-sm" data-testid="header-cta-danisma">
          {t("cta.free_consultation")}
        </Link>

        <button onClick={() => setMobileOpen((s) => !s)} className="lg:hidden text-white p-2" aria-label="Menu" data-testid="header-mobile-toggle">
          {mobileOpen ? <X size={22} /> : <Menu size={22} />}
        </button>
      </div>

      {searchOpen && <LiveSearch onClose={() => setSearchOpen(false)} onNavigate={(p) => { navigate(p); setSearchOpen(false); }} />}

      {mobileOpen && (
        <div className="lg:hidden border-t border-white/10 bg-navy" data-testid="mobile-nav">
          <nav className="container-site py-4 flex flex-col gap-1">
            {navPrimary.map((n) => (
              <NavLink
                key={n.to}
                to={n.to}
                onClick={() => setMobileOpen(false)}
                className={({ isActive }) => `flex items-center gap-2 py-2.5 px-2 text-sm ${isActive ? "text-gold" : "text-white/85"}`}
                data-testid={`mobile-nav-${n.key}`}
              >
                <n.icon size={16} /> {n.label}
              </NavLink>
            ))}
            <Link to="/iletisim" onClick={() => setMobileOpen(false)} className="btn-gold mt-3 justify-center" data-testid="mobile-cta-danisma">
              {t("cta.free_consultation")}
            </Link>
          </nav>
        </div>
      )}
    </header>
  );
}
